/* ============================================================================
   EXPENSES — money laid out on the client's behalf, logged in seconds.

   Amount, what for, a category chip, the receipt. "Save & another" keeps a
   stack of receipts moving. The studio approves what the client pays back,
   and one button turns every approved receipt into its own invoice — each
   line itemised — which then goes out like any other bill.
   ========================================================================== */
import { useState } from "react";
import { Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, money, STUDIO } from "../lib/store";
import { storeFile } from "../lib/sync";
import { fmtDate } from "../lib/format";
import { EXPENSE_CATEGORY_LABEL } from "../lib/types";
import type { Project, Expense, ExpenseCategory, ExpenseStatus } from "../lib/types";

const CATS = Object.keys(EXPENSE_CATEGORY_LABEL) as ExpenseCategory[];
const STATUS: Record<ExpenseStatus, { label: string; cls: string }> = {
  logged: { label: "Logged", cls: "" }, approved: { label: "Approved to bill", cls: "ok" }, billed: { label: "Invoiced", cls: "accent" },
  reimbursed: { label: "Reimbursed", cls: "ok" }, rejected: { label: "Studio cost", cls: "" },
};
const today = () => new Date().toISOString().slice(0, 10);
const billed = (e: Expense) => Math.round(e.amount * (1 + (e.markupPercent ?? 0) / 100) * 100) / 100;

export function ExpensesPanel({ p, canEdit, by, onInvoiced }: { p: Project; canEdit: boolean; by: string; onInvoiced?: (invoiceId: string) => void }) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [filter, setFilter] = useState<"open" | "all">("open");
  const all = p.expenses ?? [];
  const rows = all.filter((e) => filter === "all" || e.status === "logged" || e.status === "approved");
  const toBill = all.filter((e) => e.status === "approved" && e.reimbursable && !e.invoiceId);
  const cur = p.billing?.currency ?? p.currency;
  const sum = (list: Expense[]) => list.reduce((s, e) => s + billed(e), 0);

  return (
    <div className="card reveal" style={{ overflow: "hidden", marginBottom: 18 }}>
      <div className="row between wrap" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)", gap: 10 }}>
        <span>
          <span className="kicker">Reimbursable expenses</span>
          <span className="dim" style={{ display: "block", fontSize: 11.5, marginTop: 2 }}>
            {all.length ? `${all.filter((e) => e.status === "logged").length} to approve · ${toBill.length} ready to bill · ${money(sum(all.filter((e) => e.status === "billed")), cur)} invoiced, awaiting payment` : "Costs you lay out for the client — permits, printing, travel — logged with the receipt and billed back."}
          </span>
        </span>
        <span className="row wrap" style={{ gap: 6 }}>
          {all.length > 0 && <button className="chip" style={{ fontSize: 11 }} onClick={() => setFilter(filter === "open" ? "all" : "open")}>{filter === "open" ? `All · ${all.length}` : "Open only"}</button>}
          {canEdit && toBill.length > 0 && (
            <button className="btn sm accent" onClick={() => { const id = actions.invoiceExpenses(p.id, toBill.map((e) => e.id), by); if (id) onInvoiced?.(id); }} title="One invoice, each receipt its own line">
              <Icon.send width={13} />Invoice {toBill.length} · {money(sum(toBill), cur)}
            </button>
          )}
          <button className="btn sm" onClick={() => setAdding(true)}><Icon.plus width={13} />Log an expense</button>
        </span>
      </div>
      {rows.length === 0 && <div className="dim" style={{ padding: "16px 18px", fontSize: 13 }}>{all.length ? "Nothing open — every expense is invoiced or settled." : "No expenses logged yet."}</div>}
      {rows.map((e) => {
        const inv = e.invoiceId ? p.invoices.find((i) => i.id === e.invoiceId) : undefined;
        return (
          <div key={e.id} className="row between wrap" style={{ padding: "11px 18px", borderTop: "1px solid var(--line)", gap: 10, alignItems: "flex-start" }}>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span className="row wrap" style={{ gap: 8, alignItems: "baseline" }}>
                <span className="mono dim" style={{ fontSize: 11 }}>{e.ref}</span>
                <span style={{ fontSize: 13.5, fontWeight: 500 }}>{e.description}</span>
                <span className="badge" style={{ fontSize: 9 }}>{EXPENSE_CATEGORY_LABEL[e.category]}</span>
              </span>
              <span className="dim" style={{ display: "block", fontSize: 11.5, marginTop: 2 }}>
                {fmtDate(e.date)}{e.vendor ? ` · ${e.vendor}` : ""} · paid by {e.paidBy}{e.markupPercent ? ` · +${e.markupPercent}% handling` : ""}{inv ? ` · ${inv.ref}` : ""}{e.rejectedNote ? ` · ${e.rejectedNote}` : ""}
                {e.receipt && <> · <a href={e.receipt.url} target="_blank" rel="noreferrer">receipt</a></>}
              </span>
            </span>
            <span className="col" style={{ alignItems: "flex-end", gap: 5, flexShrink: 0 }}>
              <span className="serif" style={{ fontSize: 17 }}>{money(billed(e), cur)}</span>
              <span className={`badge ${STATUS[e.status].cls}`} style={{ fontSize: 9 }}>{STATUS[e.status].label}</span>
              {canEdit && (
                <span className="row wrap" style={{ gap: 4, justifyContent: "flex-end" }}>
                  {e.status === "logged" && <button className="btn sm accent" onClick={() => actions.approveExpense(p.id, e.id, by)}><Icon.check width={12} />Approve</button>}
                  {e.status === "logged" && <button className="btn ghost sm" onClick={() => actions.rejectExpense(p.id, e.id, prompt("Why is this a studio cost?") ?? undefined)}>Studio cost</button>}
                  {(e.status === "approved" || e.status === "rejected") && <button className="btn ghost sm" onClick={() => actions.reopenExpense(p.id, e.id)}>Reopen</button>}
                  {(e.status === "logged" || e.status === "approved" || e.status === "rejected") && <button className="btn ghost sm" onClick={() => setEditing(e)}><Icon.edit width={12} /></button>}
                  {(e.status === "logged" || e.status === "rejected") && <button className="btn ghost sm" onClick={() => { if (confirm(`Remove ${e.ref}?`)) actions.removeExpense(p.id, e.id); }}><Icon.close width={12} /></button>}
                </span>
              )}
            </span>
          </div>
        );
      })}
      {adding && <ExpenseModal p={p} by={by} onClose={() => setAdding(false)} />}
      {editing && <ExpenseModal p={p} by={by} existing={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ExpenseModal({ p, by, existing, onClose }: { p: Project; by: string; existing?: Expense; onClose: () => void }) {
  const lastCat = (() => { try { return localStorage.getItem("hd.expense.lastCat") as ExpenseCategory | null; } catch { return null; } })();
  const [amount, setAmount] = useState(existing ? String(existing.amount) : "");
  const [category, setCategory] = useState<ExpenseCategory>(existing?.category ?? (lastCat && CATS.includes(lastCat) ? lastCat : "other"));
  const [description, setDescription] = useState(existing?.description ?? "");
  const [date, setDate] = useState(existing?.date ?? today());
  const [vendor, setVendor] = useState(existing?.vendor ?? "");
  const [paidBy, setPaidBy] = useState(existing?.paidBy ?? by);
  const [markup, setMarkup] = useState(existing?.markupPercent ? String(existing.markupPercent) : "");
  const [reimbursable, setReimbursable] = useState(existing?.reimbursable ?? true);
  const [note, setNote] = useState(existing?.note ?? "");
  const [file, setFile] = useState<File | null>(null);
  const [more, setMore] = useState(!!existing);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);
  const [err, setErr] = useState("");
  const cur = p.billing?.currency ?? p.currency;
  const amt = Number(String(amount).replace(/[^0-9.]/g, ""));
  const ok = amt > 0 && description.trim().length > 0;

  async function save(another: boolean) {
    if (!ok || busy) return;
    setBusy(true); setErr("");
    try {
      let receipt = existing?.receipt;
      if (file) { try { receipt = await storeFile(file); } catch (e: any) { setErr(`Receipt not stored: ${String(e?.message || e)}`); } }
      const d = { date, category, description, amount: amt, vendor: vendor || undefined, paidBy, markupPercent: Number(markup) || undefined, reimbursable, receipt, note: note || undefined };
      if (existing) actions.updateExpense(p.id, existing.id, d);
      else actions.logExpense(p.id, { ...d, by });
      try { localStorage.setItem("hd.expense.lastCat", category); } catch { /* quota */ }
      if (another) { setCount((n) => n + 1); setAmount(""); setDescription(""); setVendor(""); setNote(""); setFile(null); }
      else onClose();
    } finally { setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} title={existing ? `Edit ${existing.ref}` : "Log an expense"}>
      <div className="col" style={{ gap: 12 }}>
        {count > 0 && <span className="badge ok" style={{ alignSelf: "flex-start", fontSize: 10 }}><Icon.check width={11} /> {count} logged in this sitting</span>}
        <div className="row wrap" style={{ gap: 10, alignItems: "flex-end" }}>
          <label className="col" style={{ flex: 1, minWidth: 150, gap: 4 }}><span className="dim" style={{ fontSize: 11.5 }}>Amount · {cur}</span>
            <input className="input serif" inputMode="decimal" autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" style={{ fontSize: 26, padding: "6px 12px" }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); (document.getElementById("hd-exp-desc") as HTMLInputElement | null)?.focus(); } }} />
          </label>
          <label className="col" style={{ width: 150, gap: 4 }}><span className="dim" style={{ fontSize: 11.5 }}>Date</span><input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        </div>
        <label className="col" style={{ gap: 4 }}><span className="dim" style={{ fontSize: 11.5 }}>What for</span>
          <input id="hd-exp-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Building permit, A1 plots, flight to site, 1:100 model…"
            onKeyDown={(e) => { if (e.key === "Enter" && ok) { e.preventDefault(); void save(false); } }} />
        </label>
        <div className="row wrap" style={{ gap: 6 }}>
          {CATS.map((c) => <button key={c} className={"chip" + (category === c ? " on" : "")} style={{ fontSize: 11 }} onClick={() => setCategory(c)}>{EXPENSE_CATEGORY_LABEL[c]}</button>)}
        </div>
        <label className="col" style={{ gap: 4 }}><span className="dim" style={{ fontSize: 11.5 }}>Receipt · a photo or PDF{existing?.receipt ? " · one is on file" : ""}</span>
          <input className="input" type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
        <button className="chip" style={{ alignSelf: "flex-start", fontSize: 11 }} onClick={() => setMore((v) => !v)}>{more ? "Fewer details" : "More — vendor, who paid, handling mark-up, note"}</button>
        {more && (
          <div className="col" style={{ gap: 10 }}>
            <div className="row wrap" style={{ gap: 10 }}>
              <label className="col" style={{ flex: 1, minWidth: 150, gap: 4 }}><span className="dim" style={{ fontSize: 11.5 }}>Vendor</span><input className="input" value={vendor} onChange={(e) => setVendor(e.target.value)} /></label>
              <label className="col" style={{ flex: 1, minWidth: 150, gap: 4 }}><span className="dim" style={{ fontSize: 11.5 }}>Paid by</span><input className="input" value={paidBy} onChange={(e) => setPaidBy(e.target.value)} placeholder={STUDIO.principal} /></label>
              <label className="col" style={{ width: 120, gap: 4 }}><span className="dim" style={{ fontSize: 11.5 }}>Mark-up %</span><input className="input" inputMode="decimal" value={markup} onChange={(e) => setMarkup(e.target.value)} placeholder="0" /></label>
            </div>
            <label className="row" style={{ gap: 8, fontSize: 13, cursor: "pointer" }}><input type="checkbox" checked={reimbursable} onChange={(e) => setReimbursable(e.target.checked)} />Reimbursable by the client <span className="dim">· untick for a cost the studio absorbs</span></label>
            <label className="col" style={{ gap: 4 }}><span className="dim" style={{ fontSize: 11.5 }}>Note</span><input className="input" value={note} onChange={(e) => setNote(e.target.value)} /></label>
          </div>
        )}
        {err && <span style={{ fontSize: 12, color: "var(--danger)" }}>{err}</span>}
        <div className="row wrap" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button className="btn ghost" onClick={onClose}>{count > 0 ? "Done" : "Cancel"}</button>
          {!existing && <button className="btn" disabled={!ok || busy} onClick={() => void save(true)}><Icon.plus width={14} />Save & another</button>}
          <button className="btn accent" disabled={!ok || busy} onClick={() => void save(false)}><Icon.check width={14} />{busy ? "Saving…" : "Save"}{ok ? ` · ${money(amt, cur)}` : ""}</button>
        </div>
      </div>
    </Modal>
  );
}
